/**
 * One legal document, rendered from `content.ts`.
 *
 * PUBLIC — no auth, no `useMe`, no API call. A customer deciding whether to sign up,
 * and a regulator or journalist reading the terms, both reach these pages without an
 * account, so anything here that needed a token would make the document unreadable to
 * exactly the audience it exists for.
 *
 * All prose comes from `content.ts`, which `content.test.ts` pins. Nothing
 * customer-visible is written inline in this file — same rule as the fixture-quarantine
 * console copy, and for the same reason: copy written in a component is copy no test
 * can see.
 */

import { useParams, Link } from 'react-router-dom';
import {
  LEGAL_VERSION,
  LEGAL_REVIEW_NOTE,
  findLegalDoc,
  type LegalSection,
} from './content';

/**
 * Shared page chrome, so the not-found state looks like a page and not a crash.
 * Exported because the index renders inside the same shell — two copies of the
 * public-page chrome would drift, and the logo block is the only thing telling a
 * reader arriving from a search result which company's terms these are.
 */
export function LegalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex justify-center px-4 py-10">
      <div className="w-full max-w-[760px] rounded-[24px] border border-white/[0.07] bg-brand-navy2 p-8 sm:p-11 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
        <Link to="/legal" className="inline-flex items-center gap-2.5 mb-7">
          <span className="w-8 h-8 rounded-[9px] grid place-items-center bg-gradient-to-br from-brand-teal to-brand-mint text-brand-navy font-display font-extrabold text-lg">
            P
          </span>
          <span className="font-display font-extrabold tracking-[0.06em] text-[16px]">PUNTSAVE</span>
        </Link>
        {children}
      </div>
    </div>
  );
}

function DraftChip() {
  return (
    <span className="inline-block rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-amber-300 bg-amber-300/10 border border-amber-300/30 align-middle">
      Draft
    </span>
  );
}

function Section({ section, index }: { section: LegalSection; index: number }) {
  return (
    <section className="mt-8" aria-labelledby={`legal-section-${index}`}>
      <h2
        id={`legal-section-${index}`}
        className="font-display font-bold text-[19px] leading-snug mb-3 flex flex-wrap items-center gap-2.5"
      >
        {section.heading}
        {section.draft && <DraftChip />}
      </h2>
      {section.body.map((p, i) => (
        <p key={i} className="text-white/65 text-[15px] leading-relaxed mb-3 last:mb-0">
          {p}
        </p>
      ))}
      {section.list && section.list.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {section.list.map((item, i) => (
            <li key={i} className="text-white/65 text-[15px] leading-relaxed flex gap-3">
              <span aria-hidden="true" className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-teal shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function LegalPage() {
  const { slug } = useParams<{ slug: string }>();
  const doc = findLegalDoc(slug);

  // `findLegalDoc` returns undefined rather than a default document, deliberately —
  // serving the wrong terms under a mistyped URL is worse than serving none.
  if (!doc) {
    return (
      <LegalShell>
        <h1 className="font-display font-extrabold text-[32px] leading-tight mb-3">
          We could not find that page
        </h1>
        <p className="text-white/55 text-[15px] mb-6">
          The address may be out of date. Every current policy is listed on the index.
        </p>
        <Link to="/legal" className="text-brand-teal hover:brightness-110 text-sm font-semibold">
          All policies →
        </Link>
      </LegalShell>
    );
  }

  return (
    <LegalShell>
      <span className="inline-block rounded-full px-3 py-1.5 mb-4 text-[11px] font-semibold uppercase tracking-[0.1em] text-brand-teal bg-brand-teal/10 border border-brand-teal/30">
        {doc.eyebrow}
      </span>

      <h1 className="font-display font-extrabold text-[34px] sm:text-[40px] leading-[1.05] tracking-[-0.02em] mb-2">
        {doc.title}
      </h1>
      <p className="text-white/45 text-sm mb-6">{doc.sub}</p>

      <p className="text-white/80 text-[16px] leading-relaxed border-l-2 border-brand-teal/40 pl-4">
        {doc.hero}
      </p>

      {doc.sections.map((section, i) => (
        <Section key={i} section={section} index={i} />
      ))}

      <div className="mt-10 pt-6 border-t border-white/10 flex flex-col gap-2">
        <p className="text-white/35 text-xs">{LEGAL_VERSION}</p>
        <p className="text-white/35 text-xs">{LEGAL_REVIEW_NOTE}</p>
        <p className="text-sm mt-3">
          <Link to="/legal" className="text-brand-teal hover:brightness-110">
            ← All policies
          </Link>
        </p>
      </div>
    </LegalShell>
  );
}
